import React, { useState } from 'react';
import { graphql } from 'gatsby';

import { parseAsContent } from 'utils/parser';
import themeDefault from 'components/Layout/themeDefault';
import { bgGradientBlue } from 'components/Gradients';
import Headline from 'components/Headline';
import Container from 'components/Container';
import { FormField, SubmitButton } from 'components/Form';

import {
  SectionFormStyled,
  SectionFormWrapperStyled,
  HeadlineWrapperStyled,
  FormWrapperStyled,
  GeneralMessageStyled,
} from './SectionFormStyled';

// Validation functions
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[0-9\s\-\(\)]{6,}$/;
  return phoneRegex.test(phone);
};

const validateRequired = (value) => {
  return value && value.trim().length > 0;
};

export default function SectionForm({ pageBuilder }) {
  const {
    formHeadline,
    formSuccessMessage,
    formErrorMessage,
    formSubmitButtonText,
    emailTo,
    emailSubject,
    googleSheetsUrl,
  } = pageBuilder;

  const [formData, setFormData] = useState({
    vorname: '',
    nachname: '',
    unternehmen: '',
    position: '',
    adresse: '',
    telefonnummer: '',
    email: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [generalError, setGeneralError] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!validateRequired(formData.vorname)) {
      newErrors.vorname = 'Vorname ist erforderlich';
    }

    if (!validateRequired(formData.nachname)) {
      newErrors.nachname = 'Nachname ist erforderlich';
    }

    if (!validateRequired(formData.unternehmen)) {
      newErrors.unternehmen = 'Unternehmen ist erforderlich';
    }

    if (!validateRequired(formData.position)) {
      newErrors.position = 'Position ist erforderlich';
    }

    if (!validateRequired(formData.adresse)) {
      newErrors.adresse = 'Adresse ist erforderlich';
    }

    if (!validateRequired(formData.telefonnummer)) {
      newErrors.telefonnummer = 'Telefonnummer ist erforderlich';
    } else if (!validatePhone(formData.telefonnummer)) {
      newErrors.telefonnummer = 'Bitte geben Sie eine gültige Telefonnummer ein';
    }

    if (!validateRequired(formData.email)) {
      newErrors.email = 'E-Mail ist erforderlich';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setGeneralError(false);
    setSuccess(false);

    try {
      const response = await fetch('/.netlify/functions/submit-form', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          emailTo,
          emailSubject,
          googleSheetsUrl,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setFormData({
          vorname: '',
          nachname: '',
          unternehmen: '',
          position: '',
          adresse: '',
          telefonnummer: '',
          email: '',
        });
      } else {
        setGeneralError(true);
        console.error('Form submission failed:', result.error);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setGeneralError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SectionFormStyled className={bgGradientBlue}>
      <Container>
        <SectionFormWrapperStyled>
          {formHeadline && (
            <HeadlineWrapperStyled>
              <Headline tag="h3" brush={{ color: themeDefault.primaryColor }}>
                {parseAsContent(formHeadline)}
              </Headline>
            </HeadlineWrapperStyled>
          )}

          <FormWrapperStyled>
            <form onSubmit={handleSubmit}>
              <FormField
                type="text"
                name="vorname"
                placeholder="Vorname *"
                value={formData.vorname}
                onChange={handleInputChange}
                messageError={errors.vorname}
                required
              />

              <FormField
                type="text"
                name="nachname"
                placeholder="Nachname *"
                value={formData.nachname}
                onChange={handleInputChange}
                messageError={errors.nachname}
                required
              />

              <FormField
                type="text"
                name="unternehmen"
                placeholder="Unternehmen *"
                value={formData.unternehmen}
                onChange={handleInputChange}
                messageError={errors.unternehmen}
                required
              />

              <FormField
                type="text"
                name="position"
                placeholder="Position/Funktion im Unternehmen *"
                value={formData.position}
                onChange={handleInputChange}
                messageError={errors.position}
                required
              />

              <FormField
                type="textarea"
                name="adresse"
                placeholder="Adresse *"
                value={formData.adresse}
                onChange={handleInputChange}
                messageError={errors.adresse}
                required
              />

              <FormField
                type="tel"
                name="telefonnummer"
                placeholder="Telefonnummer *"
                value={formData.telefonnummer}
                onChange={handleInputChange}
                messageError={errors.telefonnummer}
                required
              />

              <FormField
                type="email"
                name="email"
                placeholder="E-Mail *"
                value={formData.email}
                onChange={handleInputChange}
                messageError={errors.email}
                required
              />

              {success && (
                <GeneralMessageStyled type="success">
                  {formSuccessMessage || 'Ihre Anfrage wurde erfolgreich versendet.'}
                </GeneralMessageStyled>
              )}

              {generalError && (
                <GeneralMessageStyled type="error">
                  {formErrorMessage || 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.'}
                </GeneralMessageStyled>
              )}

              <SubmitButton type="submit" disabled={loading}>
                {loading ? 'Sendet...' : (formSubmitButtonText || 'Absenden')}
              </SubmitButton>
            </form>
          </FormWrapperStyled>
        </SectionFormWrapperStyled>
      </Container>
    </SectionFormStyled>
  );
}

export const query = graphql`
  fragment PageBuilderSectionFormQuery on Craft_PageBuilderSectionForm {
    id
    formHeadline
    formSuccessMessage
    formErrorMessage
    formSubmitButtonText
    emailTo
    emailSubject
    googleSheetsUrl
  }
`; 